import { Component, OnInit, Input, TemplateRef, EventEmitter, Output } from '@angular/core';
import {
  RegistrationStepService,
  StepType,
  RegistrationQuestionAnswerModel,
  RegistrationQuestionType,
  RegistrationUserQuestionsModel,
  RegistrationAnswerModel,
  ErrorStateService,
  LoggingService,
} from '@fgb/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'fgbcl-registration-questions',
  templateUrl: './registration-questions.component.html',
  styleUrls: ['./registration-questions.component.scss'],
})
export class RegistrationQuestionsComponent implements OnInit {
  readonly questionType = RegistrationQuestionType;
  stepType: StepType = StepType.Questions;
  answers: { [id: string]: any } = {};
  registrationUserQuestions$: Observable<RegistrationUserQuestionsModel>;
  questionsAndAnswers: RegistrationQuestionAnswerModel[];
  @Input() loading: TemplateRef<any>;
  @Output() answersEmitter: EventEmitter<any> = new EventEmitter();

  constructor(
    private registrationStepService: RegistrationStepService,
    private logService: LoggingService,
    public errorService: ErrorStateService
  ) {}

  ngOnInit() {
    this.registrationUserQuestions$ = this.registrationStepService.fetchRegistrationStepForCurrentWizard(this.stepType).pipe(
      map((step) => (step ? step.Data : undefined)),
      tap((questionsAnswers) => {
        this.questionsAndAnswers = questionsAnswers?.ClubQuestionAnswer;
        this._updateSelectedAnswers(questionsAnswers?.ClubQuestionAnswer);
      })
    );
  }

  submitStep() {
    const stepData = this._processAnswers(this.answers);
    if (stepData) {
      this.registrationStepService.submitRegistrationStepData(this.stepType, stepData).toPromise();
    }
  }
  /** Process the answers object into the correct type in preparation for submission to the API. */
  private _processAnswers(answers: { [id: string]: any }): RegistrationQuestionAnswerModel[] | null {
    if (!answers || Object.keys(answers).length === 0) {
      this.logService.error('No answers submitted...');
      this.errorService.addError('Please answer the required questions');
      return null;
    }

    let result: RegistrationQuestionAnswerModel[] = this.questionsAndAnswers as RegistrationQuestionAnswerModel[];

    for (const key in answers) {
      if (answers.hasOwnProperty(key)) {
        const keyParts = key.split('__');
        if (keyParts.length < 1) {
          this.errorService.addError(`Encountered invalid key format '${key}' - key should be in the format 'question__answer'`);
          return null;
        }

        const questionId: number = +keyParts[0];
        let question = this.questionsAndAnswers.find((x) => x.Question.Id === questionId);
        if (!question) {
          this.logService.error('Failed to find question with Id', questionId, 'for answer key', key);
          continue;
        }

        const answerId: number = keyParts.length > 1 ? +keyParts[1] : NaN;
        const givenAnswer = answers[key];

        let answer: RegistrationAnswerModel | undefined;

        // Update the matching question to have the correct answer specified
        switch (question.Question.QuestionType) {
          case RegistrationQuestionType.TextBox:
            question.Answer[0].Answer = givenAnswer;
            question.Answer[0].Selected = true;
            break;

          case RegistrationQuestionType.MultiSelect:
            if (!answerId) {
              this.logService.error('No answer ID provided in key for multiselect question', questionId, 'with answer key', key);
              continue;
            }

            answer = question.Answer.find((x) => x.Id === answerId);
            if (!answer) {
              this.logService.error('Failed to find answer with Id', givenAnswer, 'for answer key', key);
              continue;
            }

            answer.Selected = givenAnswer;
            break;

          case RegistrationQuestionType.DropDown:
          case RegistrationQuestionType.Radio:
            question.Answer.forEach((x) => (x.Selected = false));

            answer = question.Answer.find((x) => x.Id === +givenAnswer);
            if (!answer) {
              if (+givenAnswer) {
                this.logService.error('Failed to find answer with Id', givenAnswer, 'for answer key', key);
              }
              continue;
            }

            answer.Selected = true;
            break;
        }
      }
    }

    return result;
  }
  
  /** Convert a list of RegistrationQuestionAnswerModel returned from the API into the answers object. */
  private _updateSelectedAnswers(questionsAndAnswers: RegistrationQuestionAnswerModel[]): void {
    questionsAndAnswers &&
      questionsAndAnswers.forEach((questionAndAnswer) => {
        const key = `${questionAndAnswer.Question.Id}`;
        switch (questionAndAnswer.Question.QuestionType) {
          case RegistrationQuestionType.TextBox:
            this.answers[key] = questionAndAnswer.Answer[0].Answer;
            break;
          case RegistrationQuestionType.MultiSelect:
            questionAndAnswer.Answer.forEach((answer) => {
              this.answers[`${key}__${answer.Id}`] = answer.Selected;
            });
            break;
          case RegistrationQuestionType.DropDown:
          case RegistrationQuestionType.Radio:
            this.answers[key] = (questionAndAnswer.Answer.find((x) => x.Selected) || { Id: 0 }).Id;
            break;
        }
      });
  }

  handleAnswerChange() {
    this.answersEmitter.emit(this.answers);
  }
}
